/**
 * @file Classe de base des modes : machine a etats generique (intro/running/won/lost/result), chrono, evaluation victoire/defaite data-driven, spawn par regles.
 */
import type { ModeId } from '@shared/enums';
import { fail } from '@shared/types';
import type { Result } from '@shared/types';
import type { ModeDef, ModeParamsMap, SpawnEntry } from '@config/types';
import type { SimContext } from '../Context';
import { isShootable } from '../combat/damage';
import { spawnTarget } from '../ecs/EntityFactory';
import type { With } from '../ecs/World';
import type { ModeCommand, ModeExitReason, ModeHud, ModeInstance, ModePhase, ModeResult, ModeStartParams } from './ModeTypes';
import { layoutPositions, pickSpawnEntry } from './spawnLayout';

export const INTRO_SECONDS = 1.2;
export const RESULT_SECONDS = 2.5;

export abstract class BaseMode<M extends ModeId = ModeId> implements ModeInstance {
  private _phase: ModePhase = 'idle';
  protected timeLeft: number | null = null;
  protected timeTotal: number | null = null;
  protected introLeft = 0;
  protected resultLeft = 0;
  protected score = 0;
  protected kills = 0;
  protected spawnedCount = 0;
  protected bossKilled = false;
  protected wave = 0;
  protected barrierHp = 0;
  protected barrierMax = 0;
  protected startParams: ModeStartParams = {};
  protected lastResult: ModeResult | null = null;

  constructor(readonly def: ModeDef<M>) {}

  get id(): ModeId {
    return this.def.id;
  }
  get phase(): ModePhase {
    return this._phase;
  }
  get params(): ModeParamsMap[M] {
    return this.def.params;
  }
  get knockback(): number {
    return 0;
  }

  // ------------------------------------------------------------------ cycle de vie (ne pas surcharger)

  enter(ctx: SimContext, params: ModeStartParams): void {
    this.startParams = params;
    this.score = 0;
    this.kills = 0;
    this.spawnedCount = 0;
    this.bossKilled = false;
    this.wave = 0;
    this.lastResult = null;
    const t = this.def.rules.timer;
    this.timeTotal = t.kind === 'countdown' ? t.seconds : null;
    this.timeLeft = this.timeTotal;
    this.onEnter(ctx, params);
    this.beginIntro(ctx);
  }

  update(ctx: SimContext, dt: number): void {
    switch (this._phase) {
      case 'intro':
        this.introLeft -= dt;
        if (this.introLeft <= 0) {
          this.setPhase(ctx, 'running');
          this.onStart(ctx);
        }
        break;
      case 'running': {
        if (this.timeLeft !== null) this.timeLeft = Math.max(0, this.timeLeft - dt);
        this.onRunning(ctx, dt);
        const outcome = this.evaluateOutcome(ctx);
        if (outcome) {
          this.setPhase(ctx, outcome);
          this.resultLeft = RESULT_SECONDS;
          if (outcome === 'won') this.onWin(ctx);
          else this.onLose(ctx);
        }
        break;
      }
      case 'won':
      case 'lost':
        this.resultLeft -= dt;
        if (this.resultLeft <= 0) {
          this.setPhase(ctx, 'result');
          this.onResult(ctx);
        }
        break;
      default:
        break;
    }
  }

  exit(ctx: SimContext, reason: ModeExitReason): ModeResult | null {
    const r = this.onExit(ctx, reason);
    this.clearArena(ctx); // le mode suivant demarre sur une arene vide (cibles et projectiles de ce mode retires)
    this.setPhase(ctx, 'idle');
    return r ?? this.lastResult;
  }

  addScore(points: number): void {
    this.score += points;
  }

  onTargetKilled(ctx: SimContext, target: With<'target' | 'health'>): void {
    this.kills++;
    if (target.target.isBoss) this.bossKilled = true;
    this.onKill(ctx, target);
  }

  handleCommand(_ctx: SimContext, _cmd: ModeCommand): Result {
    return fail('wrongMode');
  }

  getHud(ctx: SimContext): ModeHud {
    return {
      mode: this.id,
      phase: this._phase,
      timeLeft: this.timeLeft,
      timeTotal: this.timeTotal,
      progress: null,
      titleKey: `mode.${this.id}.title`,
      titleParams: {},
      boss: null,
      barrier: this.barrierMax > 0 ? { hp: this.barrierHp, max: this.barrierMax } : null,
      wave: this.wave > 0 ? this.wave : null,
      score: this.def.rules.killRewards === 'score' ? this.score : null,
      canChallenge: false,
      introLeft: Math.max(0, this.introLeft),
      bossInfo: null,
      ...this.hudExtras(ctx),
    };
  }

  // ------------------------------------------------------------------ hooks a surcharger

  protected onEnter(_ctx: SimContext, _params: ModeStartParams): void {}
  protected onStart(_ctx: SimContext): void {}
  protected onRunning(_ctx: SimContext, _dt: number): void {}
  protected onKill(_ctx: SimContext, _target: With<'target' | 'health'>): void {}
  protected onWin(_ctx: SimContext): void {}
  protected onLose(_ctx: SimContext): void {}
  /** Fin de l'ecran de resultat : par defaut, retour au mode Main (ModeManager.returnHome). */
  protected onResult(ctx: SimContext): void {
    ctx.modes.returnHome('completed');
  }
  protected onExit(_ctx: SimContext, _reason: ModeExitReason): ModeResult | null {
    return null;
  }
  protected hudExtras(_ctx: SimContext): Partial<ModeHud> {
    return {};
  }

  // ------------------------------------------------------------------ utilitaires communs

  protected setPhase(ctx: SimContext, p: ModePhase): void {
    if (this._phase === p) return;
    this._phase = p;
    ctx.bus.emit('ModePhaseChanged', { mode: this.id, phase: p });
  }

  /** (Re)lance l'intro : utilise par Main entre deux niveaux sans quitter le mode. */
  protected beginIntro(ctx: SimContext, seconds = INTRO_SECONDS): void {
    this.introLeft = seconds;
    this.setPhase(ctx, 'intro');
  }

  /** Nombre de cibles encore vivantes et tirables. */
  protected aliveShootable(ctx: SimContext): number {
    let n = 0;
    for (const e of ctx.rt.world.ofKind('target')) if (isShootable(ctx, e)) n++;
    return n;
  }

  /** Evaluation generique a partir de ModeRules.win / ModeRules.lose (les sous-classes peuvent surcharger). */
  protected evaluateOutcome(ctx: SimContext): 'won' | 'lost' | null {
    const { lose } = this.def.rules;
    switch (lose.kind) {
      case 'timerExpired':
        if (this.timeLeft !== null && this.timeLeft <= 0 && !this.isWon(ctx)) return 'lost';
        break;
      case 'barrierBroken':
        if (this.barrierMax > 0 && this.barrierHp <= 0) return 'lost';
        break;
      default:
        break;
    }
    if (this.isWon(ctx)) return 'won';
    return null;
  }

  protected isWon(ctx: SimContext): boolean {
    const { win } = this.def.rules;
    switch (win.kind) {
      case 'killAll':
        return this.spawnedCount > 0 && this.aliveShootable(ctx) === 0;
      case 'killCount':
        return this.kills >= win.count;
      case 'killBoss':
        return this.bossKilled;
      case 'surviveWaves':
        return this.wave > win.waves;
      case 'timerEnd':
        return this.timeLeft !== null && this.timeLeft <= 0;
      case 'never':
      default:
        return false;
    }
  }

  /** Spawne `count` cibles selon ModeRules.spawn (table + disposition), au palier `stageValue`. */
  protected spawnFromRules(ctx: SimContext, count: number, stageValue: number, table?: readonly SpawnEntry[]): number {
    const rules = this.def.rules;
    const tbl = table ?? (rules.spawn.table === 'biome' ? ctx.config.biomes[ctx.rt.biome].treeTable : rules.spawn.table);
    const avoid = ctx.rt.catIds.map((id) => {
      const t = ctx.rt.world.get(id)?.transform;
      return { x: t?.x ?? 0, y: t?.y ?? 0, r: 2.5 };
    });
    for (const e of ctx.rt.world.ofKind('target')) avoid.push({ x: e.transform.x, y: e.transform.y, r: e.collider?.radius ?? 1 });
    const n = Math.min(count, rules.spawn.maxAlive - ctx.rt.world.count('target'));
    if (n <= 0) return 0;
    const positions = layoutPositions(ctx.rng.spawn, { layout: rules.spawn.layout, arena: rules.arena, count: n, minSpacing: 2.2, avoid });
    let spawned = 0;
    for (const p of positions) {
      const entry = pickSpawnEntry(ctx.rng.spawn, tbl, stageValue);
      if (!entry) break;
      spawnTarget(ctx, ctx.index.target(entry.targetId), p.x, p.y, { stageValue });
      spawned++;
    }
    this.spawnedCount += spawned;
    return spawned;
  }

  /** Detruit toutes les cibles et projectiles (changement de niveau/mode). */
  protected clearArena(ctx: SimContext): void {
    for (const e of ctx.rt.world.ofKind('target')) ctx.rt.targetGrid.remove(e.id);
    ctx.rt.world.clearKind('target');
    ctx.rt.world.clearKind('projectile');
  }
}
